import React from 'react';
import {
  LineChart,
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  ForecastDataPoint,
  HistoricalDataPoint,
  formatCurrency,
  formatDate,
  getModelLabel,
} from '../services/forecastingService';

interface ForecastChartProps {
  historical?: HistoricalDataPoint[];
  forecasts?: ForecastDataPoint[];
  model?: 'linear' | 'moving_average' | 'exponential' | 'hybrid';
  showConfidenceBands?: boolean;
  currency?: string;
  height?: number;
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  historical?: number;
  predicted?: number;
  confidenceLow?: number;
  confidenceHigh?: number;
}

const ForecastChart: React.FC<ForecastChartProps> = ({
  historical = [],
  forecasts = [],
  model = 'linear',
  showConfidenceBands = true,
  currency = 'USD',
  height = 400,
}) => {
  // Combine historical and forecast data for visualization
  const chartData: ChartDataPoint[] = React.useMemo(() => {
    const data: ChartDataPoint[] = [];

    // Add historical data
    historical.forEach((point) => {
      data.push({
        date: point.date,
        displayDate: formatDate(point.date),
        historical: point.revenue,
      });
    });

    // Add forecast data
    forecasts.forEach((point) => {
      data.push({
        date: point.date,
        displayDate: formatDate(point.date),
        predicted: point.predictedRevenue,
        confidenceLow: point.confidenceLow,
        confidenceHigh: point.confidenceHigh,
      });
    });

    // Sort by date
    return data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [historical, forecasts]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{data.displayDate}</p>
        {data.historical !== undefined && (
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Actual: {formatCurrency(data.historical, currency)}
            </span>
          </div>
        )}
        {data.predicted !== undefined && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Predicted: {formatCurrency(data.predicted, currency)}
              </span>
            </div>
            {showConfidenceBands && data.confidenceLow !== undefined && data.confidenceHigh !== undefined && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <div>Low: {formatCurrency(data.confidenceLow, currency)}</div>
                <div>High: {formatCurrency(data.confidenceHigh, currency)}</div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // Find the transition point between historical and forecast
  const transitionIndex = historical.length > 0 ? historical.length - 1 : 0;

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {getModelLabel(model)} Forecast
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Revenue prediction with confidence intervals
        </p>
      </div>

      <ResponsiveContainer width="100%" height={height}>
        {showConfidenceBands ? (
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="confidenceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              tickFormatter={(value: number) => formatCurrency(value, currency)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 14 }} />

            {/* Confidence band area */}
            <Area
              type="monotone"
              dataKey="confidenceHigh"
              stroke="none"
              fill="url(#confidenceGradient)"
              fillOpacity={1}
              name="Confidence Range"
              isAnimationActive={true}
            />
            <Area
              type="monotone"
              dataKey="confidenceLow"
              stroke="none"
              fill="#ffffff"
              fillOpacity={1}
              isAnimationActive={true}
            />

            {/* Historical revenue line */}
            <Line
              type="monotone"
              dataKey="historical"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Historical Revenue"
              isAnimationActive={true}
            />

            {/* Predicted revenue line */}
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4 }}
              name="Predicted Revenue"
              isAnimationActive={true}
            />

            {/* Reference line at transition point */}
            {transitionIndex > 0 && (
              <ReferenceLine
                x={chartData[transitionIndex]?.displayDate}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'Today', position: 'top', fill: '#f59e0b' }}
              />
            )}
          </AreaChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              tickFormatter={(value: number) => formatCurrency(value, currency)}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 14 }} />

            {/* Historical revenue line */}
            <Line
              type="monotone"
              dataKey="historical"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Historical Revenue"
              isAnimationActive={true}
            />

            {/* Predicted revenue line */}
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="#10b981"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ r: 4 }}
              name="Predicted Revenue"
              isAnimationActive={true}
            />

            {/* Reference line at transition point */}
            {transitionIndex > 0 && (
              <ReferenceLine
                x={chartData[transitionIndex]?.displayDate}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'Today', position: 'top', fill: '#f59e0b' }}
              />
            )}
          </LineChart>
        )}
      </ResponsiveContainer>

      {/* Chart legend information */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-blue-500"></div>
          <span className="text-gray-600 dark:text-gray-400">Historical Data</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-green-500 border-dashed border-t-2 border-green-500"></div>
          <span className="text-gray-600 dark:text-gray-400">Forecast</span>
        </div>
        {showConfidenceBands && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-green-200 dark:bg-green-900 opacity-30"></div>
            <span className="text-gray-600 dark:text-gray-400">Confidence Range</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForecastChart;
